import filter from 'lodash/fp/filter';
import isUndefined from 'lodash/fp/isUndefined';
import { convertSecondsIntoHHmm, coordinatesToCoords } from '../../common/utils';
import type {
    ChargingStopWaypoint,
    EventArrival,
    EventChargeStop,
    EventDeparture,
    EventIntermediateStop,
    StopoverWaypoint,
    Waypoint as TourWaypoint,
} from '../../store/facade/facadeApiV3';
import { TimelineEventType, WaypointType } from '../../store/facade/facadeApiV3';
import { type Waypoint, WaypointType as LocalWaypointType } from '../../store/search/searchSlice';
import { WaypointBuilder } from '../../store/search/WaypointBuilder';
import type { Itinerary } from '../../store/tour/tourSlice';

const buildWaypointFromTourChargingStop = (tw: ChargingStopWaypoint, id: number): Waypoint => {
    const builder = new WaypointBuilder();
    builder.withId(id);
    builder.withType(LocalWaypointType.CHARGING_STATION);
    builder.withAddressLabel(tw.name ?? '');
    if (tw.address) {
        builder.withAddress(tw.address);
    }
    if (tw.coordinates) {
        builder.withCoordinates(coordinatesToCoords(tw.coordinates));
    }
    if (tw.stationId) {
        builder.withStationId(tw.stationId);
    }
    if (tw.power) {
        builder.withMaxPower(tw.power);
    }
    if (tw.voltage) {
        builder.withVoltage(tw.voltage);
    }
    if (tw.current) {
        builder.withCurrent(tw.current);
    }
    return builder.build();
};

const buildWaypointFromTourStopover = (tw: StopoverWaypoint, id: number): Waypoint => {
    const builder = new WaypointBuilder();
    builder.withId(id);
    builder.withType(LocalWaypointType.ADDITIONAL_STOP);
    builder.withAddressLabel(tw.name ?? '');
    if (tw.address) {
        builder.withAddress(tw.address);
    }
    if (tw.coordinates) {
        builder.withCoordinates(coordinatesToCoords(tw.coordinates));
    }
    if (tw.duration) {
        builder.withTimeAtStopover(convertSecondsIntoHHmm(tw.duration));
    }
    return builder.build();
};

const buildDepartureWaypoint = (
    builder: WaypointBuilder,
    details: EventDeparture,
    index: number,
    orderedWaypoints: Waypoint[]
) => {
    if (details.address) {
        builder.withAddress(details.address);
    }
    if (details.coordinates) {
        builder.withCoordinates(coordinatesToCoords(details.coordinates));
    }
    builder.withId(index);

    const departure = orderedWaypoints.at(0);
    if (departure?.type === LocalWaypointType.VEHICLE_LOCATION) {
        builder.withType(LocalWaypointType.VEHICLE_LOCATION);
        builder.withAddressLabel(departure.addressLabel ?? '');
    } else {
        builder.withAddressLabel(details.address?.name ?? '');
    }
};

const buildArrivalWaypoint = (builder: WaypointBuilder, details: EventArrival, index: number) => {
    if (details.address) {
        builder.withAddress(details.address);
    }
    builder.withAddressLabel(details.address?.name ?? '');
    if (details.coordinates) {
        builder.withCoordinates(coordinatesToCoords(details.coordinates));
    }
    builder.withId(index);
    builder.withIsInRange(Boolean(details.expectedBatteryLevelAtArrival && details.expectedBatteryLevelAtArrival > 0));
};

const buildIntermediateStopWaypoint = (
    builder: WaypointBuilder,
    details: EventIntermediateStop,
    index: number,
    cargoOnIntermediateStops: number[],
    intermediateStopIndex: number
) => {
    if (details.address) {
        builder.withAddress(details.address);
    }
    builder.withAddressLabel(details.address?.name ?? '');
    if (details.coordinates) {
        builder.withCoordinates(coordinatesToCoords(details.coordinates));
    }
    builder.withId(index);
    builder.withIsInRange(Boolean(details.expectedBatteryLevelAtArrival && details.expectedBatteryLevelAtArrival > 0));
    builder.withCargoAtDeparture(cargoOnIntermediateStops.at(intermediateStopIndex) ?? 0);

    if (!isUndefined(details.duration)) {
        const timeDisplay = convertSecondsIntoHHmm(details.duration);
        builder.withTimeAtStopover(timeDisplay);
    }
};

const buildChargeStopWaypoint = (builder: WaypointBuilder, details: EventChargeStop, index: number) => {
    if (details.address) {
        builder.withAddress(details.address);
    }
    builder.withAddressLabel(details.address?.name ?? '');
    if (details.coordinates) {
        builder.withCoordinates(coordinatesToCoords(details.coordinates));
    }
    builder.withId(index);
    builder.withType(LocalWaypointType.CHARGING_STATION);

    builder.withIsInRange(Boolean(details.expectedBatteryLevelAtArrival && details.expectedBatteryLevelAtArrival > 0));

    if (!isUndefined(details.maxDuration)) {
        const timeDisplay = convertSecondsIntoHHmm(details.maxDuration);
        builder.withTimeAtStopover(timeDisplay);
    }

    if (details.stationId) {
        builder.withStationId(details.stationId);
    }
    if (!isUndefined(details.power)) {
        builder.withMaxPower(details.power);
    }
    if (!isUndefined(details.current)) {
        builder.withMaxPower(details.current);
    }
    if (!isUndefined(details.voltage)) {
        builder.withMaxPower(details.voltage);
    }
};

const excludedTypes = [TimelineEventType.Driving, TimelineEventType.Transit, TimelineEventType.Via];

export const mapItineraryIntoWaypoints = (
    currentItinerary: Itinerary,
    orderedWaypoints: Waypoint[],
    initialCargoWeight?: number,
    tourWaypoints?: TourWaypoint[]
): Waypoint[] => {
    if (!currentItinerary.events?.timelineEvents) {
        return [];
    }

    const events = filter(
        (
            event: EventArrival | EventDeparture | EventIntermediateStop | EventChargeStop
        ): event is EventArrival | EventDeparture | EventIntermediateStop | EventChargeStop =>
            !excludedTypes.includes(event.timelineEventType)
    )(currentItinerary.events.timelineEvents);

    const cargoOnIntermediateStops = currentItinerary.events.timelineEvents
        .filter(
            (event): event is EventIntermediateStop => event.timelineEventType === TimelineEventType.IntermediateStop
        )
        .map(event => event.weightDelta)
        .filter((weightDelta): weightDelta is number => weightDelta !== undefined)
        .reduce(
            (acc, curr) => {
                const lastValue = acc.at(-1) ?? 0;
                acc.push(lastValue - curr);
                return acc;
            },
            [initialCargoWeight ?? 0]
        )
        .slice(1);

    const newWaypoints: Waypoint[] = [];
    for (let index = 0, intermediateStop = 0; index < events.length; index++) {
        const event = events[index];

        if (isUndefined(event)) {
            break;
        }

        const builder = new WaypointBuilder();
        switch (event.timelineEventType) {
            case TimelineEventType.Arrival:
                buildArrivalWaypoint(builder, event, index);
                break;
            case TimelineEventType.Departure:
                buildDepartureWaypoint(builder, event, index, orderedWaypoints);
                break;
            case TimelineEventType.IntermediateStop:
                buildIntermediateStopWaypoint(builder, event, index, cargoOnIntermediateStops, intermediateStop);
                ++intermediateStop;
                break;
            case TimelineEventType.ChargeStop:
                buildChargeStopWaypoint(builder, event, index);
                break;
        }

        newWaypoints.push(builder.build());
    }

    // Insert CHARGING_STOP and STOPOVER that aren't in timeline events
    tourWaypoints?.forEach(tw => {
        if (!tw.coordinates || (tw.coordinates.latitude === 0 && tw.coordinates.longitude === 0)) {
            return;
        }

        const existsInTimeline = events.some(event => {
            if (
                tw.waypointType === WaypointType.ChargingStop &&
                event.timelineEventType === TimelineEventType.ChargeStop
            ) {
                return event.stationId === tw.stationId;
            }
            if (
                tw.waypointType === WaypointType.Stopover &&
                event.timelineEventType === TimelineEventType.IntermediateStop
            ) {
                return (
                    Math.abs((event.coordinates?.latitude ?? 0) - tw.coordinates.latitude) < 0.0001 &&
                    Math.abs((event.coordinates?.longitude ?? 0) - tw.coordinates.longitude) < 0.0001
                );
            }
            return false;
        });

        const existsInNewWaypoints = newWaypoints.some(
            wp =>
                Math.abs((wp.coordinates?.lat ?? 0) - tw.coordinates.latitude) < 0.0001 &&
                Math.abs((wp.coordinates?.lng ?? 0) - tw.coordinates.longitude) < 0.0001
        );

        if (!existsInTimeline && !existsInNewWaypoints) {
            let waypoint: Waypoint | null = null;
            if (tw.waypointType === WaypointType.ChargingStop) {
                waypoint = buildWaypointFromTourChargingStop(tw, newWaypoints.length);
            } else if (tw.waypointType === WaypointType.Stopover) {
                waypoint = buildWaypointFromTourStopover(tw, newWaypoints.length);
            }

            if (waypoint?.coordinates && (waypoint.coordinates.lat !== 0 || waypoint.coordinates.lng !== 0)) {
                newWaypoints.splice(-1, 0, waypoint);
            }
        }
    });

    return newWaypoints;
};
